import type { PostingDiscoveryReq } from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import { type Filter, FilterId } from '../../../filters/types/filters';
import { buildCompanyFilter, buildMultiCompanyFilter } from './company';
import { buildKeywordsFilter, buildTitlesFilter } from './free-text-filters';
import { buildGeographyFilter, buildMultiGeographyFilter } from './geography';
import {
  buildMultiSeniorityFilter,
  buildSeniorityFilter,
} from './list-filters';
import { composeFilterNodes } from './node-builders';
import {
  buildMultiSalaryFilter,
  buildPostDateFilter,
  buildSalaryFilter,
} from './range-filters';
import {
  buildMultiRoleFilter,
  buildRoleFilter,
  getRoleTaxonomyNumber,
} from './roles';
import {
  buildCustomSkillFilter,
  buildMultiCustomSkillFilter,
  getSkillTaxonomyId,
} from './skills';

const DEFAULT_PAGE_SIZE = 1000;

export const buildPostingDiscoveryFilters = (
  filters: Filter[]
): PostingDiscoveryReq => {
  const hasMultiSeniority = findFilterById(filters, FilterId.MULTI_SENIORITY);
  const hasMultiCustomSkill = findFilterById(
    filters,
    FilterId.MULTI_CUSTOM_SKILL
  );
  const hasMultiPay = findFilterById(filters, FilterId.MULTI_PAY);

  const seniorityFilter = hasMultiSeniority
    ? null
    : buildSeniorityFilter(filters);
  const customSkillFilter = hasMultiCustomSkill
    ? null
    : buildCustomSkillFilter(filters);
  const salaryFilter = hasMultiPay ? null : buildSalaryFilter(filters);

  // Tree filters
  const companyFilter = buildCompanyFilter(filters);
  const geographyFilter = buildGeographyFilter(filters);
  const roleFilter = buildRoleFilter(filters);

  // Free text filters
  const keywordsFilter = buildKeywordsFilter(filters);
  const titlesFilter = buildTitlesFilter(filters);

  // Date filter
  const postDateFilter = buildPostDateFilter(filters);

  // Multi filters
  const multiCompanyFilter = buildMultiCompanyFilter(filters);
  const multiGeographyFilter = buildMultiGeographyFilter(filters);
  const multiRoleFilter = buildMultiRoleFilter(filters);
  const multiCustomSkillFilter = buildMultiCustomSkillFilter(filters);
  const multiSeniorityFilter = buildMultiSeniorityFilter(filters);
  const multiSalaryFilter = buildMultiSalaryFilter(filters);

  const allFilterNodes = [
    // Single filters
    seniorityFilter,
    customSkillFilter,
    salaryFilter,
    companyFilter,
    geographyFilter,
    roleFilter,
    keywordsFilter,
    titlesFilter,
    postDateFilter,
    // Multi filters
    multiCompanyFilter,
    multiGeographyFilter,
    multiRoleFilter,
    multiCustomSkillFilter,
    multiSeniorityFilter,
    multiSalaryFilter,
  ];

  const expr = composeFilterNodes(allFilterNodes);

  return {
    filters: {
      expr,
      roles_tax: getRoleTaxonomyNumber(filters),
      skills_tax: getSkillTaxonomyId(filters),
    },
    options: {
      include_descriptions: true,
    },
    pagination: {
      page_num: 1,
      page_size: DEFAULT_PAGE_SIZE,
    },
  };
};
